import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getHomeRoleSwitcherOptions,
  isPreviewHomeRole,
  readActiveHomeRole,
  writeActiveHomeRole,
} from "../utils/homeRole";

const ROLE_I18N = {
  admin: "roleAdmin",
  coach: "roleCoach",
  player: "rolePlayer",
  monitor: "roleMonitor",
  committee: "roleCommittee",
  parent: "parentRoleLabel",
  moderator: "roleModerator",
};

const ROLE_FALLBACK = {
  admin: "Administrator",
  coach: "Coach",
  player: "Player",
  monitor: "Monitor",
  committee: "Committee",
  parent: "Parent / Tutor",
  moderator: "Moderator",
};

export function homeRoleLabel(t, role, user) {
  const key = ROLE_I18N[role] || "rolePlayer";
  const base = t(key, { defaultValue: ROLE_FALLBACK[role] || role });
  if (user && isPreviewHomeRole(user, role)) {
    return t("viewAsRole", { role: base, defaultValue: `View as ${base}` });
  }
  return base;
}

/**
 * Current home role as a label. Dual-role accounts can switch from here
 * (moved off the home dashboard).
 */
export function HomeRolePicker({ user, className = "" }) {
  const { t } = useTranslation();
  const roles = useMemo(
    () => getHomeRoleSwitcherOptions(user),
    [user?.user_id, user?.type_user_id, user?.all_roles, user?.hasCoachRole, user?.hasAdminRole, user?.hasParentRole, user?.hasModeratorRole]
  );
  const [role, setRole] = useState(() => readActiveHomeRole(user));

  useEffect(() => {
    setRole(readActiveHomeRole(user));
  }, [user?.user_id]);

  useEffect(() => {
    const sync = () => setRole(readActiveHomeRole(user));
    window.addEventListener("smarttt-home-role-changed", sync);
    return () => window.removeEventListener("smarttt-home-role-changed", sync);
  }, [user?.user_id]);

  const current = roles.includes(role) ? role : roles[0] || "player";
  const canSwitch = roles.length > 1;

  const onChange = (next) => {
    if (!next || next === current) return;
    setRole(next);
    writeActiveHomeRole(next);
  };

  return (
    <div className={`profile-role ${className}`.trim()}>
      {canSwitch ? (
        <label className="profile-role__switch">
          <span className="profile-role__legend">{t("profileRole")}</span>
          <select
            className="profile-role__select"
            value={current}
            onChange={(event) => onChange(event.target.value)}
            aria-label={t("profileSwitchRole")}
          >
            {roles.map((id) => (
              <option key={id} value={id}>
                {homeRoleLabel(t, id, user)}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <span className="profile-role__label">{homeRoleLabel(t, current, user)}</span>
      )}
    </div>
  );
}
